import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { td } from '@/lib/i18n';
import { errorHandler } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { mapWorkspaceTemplate, useSystemTemplates, useWorkspaceTemplates, } from '../hooks/use-templates';
import { useDeleteWorkspaceTemplate } from '../hooks/use-save-as-template';
import { TemplateCard } from './template-card';
const FILTER_TAGS = [
    'all',
    'food',
    'hospitality',
    'retail',
    'automotive',
    'real-estate',
    'services',
    'events',
    'app',
];
export function TemplateGallery({ proUnlocked, planFlags, workspaceId, canDeleteWorkspaceTemplates = false, onPick, }) {
    const { t } = useTranslation('campaign');
    const { data: systemTemplates = [], isPending: systemPending } = useSystemTemplates();
    const { data: workspaceRaw = [], isPending: workspacePending } = useWorkspaceTemplates(workspaceId);
    const [filter, setFilter] = React.useState('all');
    const { mutate: deleteTemplate, isPending: isDeleting } = useDeleteWorkspaceTemplate();
    // Workspace templates land at the top of the gallery; system templates
    // follow. `blank` (system) is special-cased to always appear first so the
    // "start from scratch" affordance never disappears behind a filter.
    const merged = React.useMemo(() => {
        const workspace = workspaceRaw.map(mapWorkspaceTemplate);
        const systemBlank = systemTemplates.find((t) => t.id === 'blank');
        const systemRest = systemTemplates.filter((t) => t.id !== 'blank');
        return [
            ...(systemBlank ? [systemBlank] : []),
            ...workspace,
            ...systemRest,
        ];
    }, [systemTemplates, workspaceRaw]);
    const visible = React.useMemo(() => {
        if (filter === 'all')
            return merged;
        // Workspace templates and `blank` are always visible — they have no
        // industry tags by design.
        return merged.filter((tpl) => tpl.id === 'blank' || tpl.source === 'workspace' || tpl.industryTags.includes(filter));
    }, [merged, filter]);
    const isPending = systemPending || (Boolean(workspaceId) && workspacePending);
    const handleDelete = React.useCallback((template) => {
        if (!workspaceId || template.source !== 'workspace')
            return;
        deleteTemplate({ workspaceId, publicId: template.id }, {
            onSuccess: (data) => toast.success(data.message),
            onError: errorHandler,
        });
    }, [deleteTemplate, workspaceId]);
    return (<Card className="w-full">
            <CardHeader>
                <CardTitle>{t('template_gallery.title')}</CardTitle>
                <CardDescription>{t('template_gallery.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                    {FILTER_TAGS.map((tag) => {
            const active = filter === tag;
            return (<button key={tag} type="button" onClick={() => setFilter(tag)} className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-colors', active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground')}>
                                {td(t, `template_gallery.filter.${tag.replace('-', '_')}`)}
                            </button>);
        })}
                </div>

                {isPending ? (<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="space-y-2">
                                <Skeleton className="aspect-[5/3] w-full"/>
                                <Skeleton className="h-4 w-2/3"/>
                                <Skeleton className="h-3 w-full"/>
                            </div>))}
                    </div>) : (<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {visible.map((template) => (<TemplateCard key={`${template.source}:${template.id}`} template={template} proUnlocked={proUnlocked} planFlags={planFlags} onPick={onPick} onDelete={canDeleteWorkspaceTemplates && template.source === 'workspace' && !isDeleting
                    ? () => handleDelete(template)
                    : undefined}/>))}
                    </div>)}
            </CardContent>
        </Card>);
}
